import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import Lottie from 'lottie-react';
import { CustomBtn } from '../CustomBtn/CustomBtn';
import {
  getEventLogger,
  notifyAppThatUserHasRegistered,
  copyToClipboard,
} from '../../../utils/utils';
import styles from './SuccessfulPayment.scss';
import newOrderConfirmationPageBannerImg from '../../../../../myndlift-signup/public/assets/new_order_confirmation_page_banner_img.png';
import downloadOnTheAppStoreImg from '../../../../../myndlift-signup/public/assets/download_on_the_app_store.png';
import getItOnGooglePlayImg from '../../../../../myndlift-signup/public/assets/get_it_on_google_play.png';
import featherCopyIcon from '../../../../../myndlift-signup/public/assets/feather_copy_icon.png';
import confetti from '../../../../../myndlift-signup/public/assets/lottie/confetti.json';
import confettiMobile from '../../../../../myndlift-signup/public/assets/lottie/confetti_anim_mobile.json';
import { useMediaMatch } from '../../../Core/hooks/useMediaMatch';
import {
  xsExtraMediaMaxWidth,
  mdExtraMediaMaxWidth,
} from '../../../cssInJs/constants';

const SuccessfulPaymentBase = ({
  // onVisitBlog,
  // hasOnlySoftwarePackage,
  onAddMoreUser,
  onComponentReadyToRender,
  activationCode,
}) => {
  const wasComponentReadyToRenderFuncCalledRef = useRef(false);
  if (!wasComponentReadyToRenderFuncCalledRef.current) {
    onComponentReadyToRender();
    notifyAppThatUserHasRegistered(activationCode);
    wasComponentReadyToRenderFuncCalledRef.current = true;
  }

  const [isCodeCopied, setIsCodeCopied] = useState(false);

  const [isMatchingXSExtraScreen] = useMediaMatch({
    mediaMatchString: `(max-width: ${xsExtraMediaMaxWidth}px)`,
  });

  const [isMatchingMDExtraScreen] = useMediaMatch({
    mediaMatchString: `(max-width: ${mdExtraMediaMaxWidth}px)`,
  });

  const orderConfirmationPageEventLogger = getEventLogger({
    prefix: 'order_confirmation',
  });

  return (
    <>
      <Lottie
        animationData={isMatchingMDExtraScreen ? confettiMobile : confetti}
        loop={false}
        autoplay
        className={styles.confetti_lottie_background}
      />
      <div className={styles.root}>
        <div className={styles.container}>
          <div className={styles.banner_img_container}>
            <img
              src={newOrderConfirmationPageBannerImg}
              className={styles.banner_img_style}
            />
            <p className={styles.banner_image_text}>
              We’re excited to have you onboard, {sessionStorage.newUserName}
            </p>
          </div>
          <div className={styles.body_content}>
            <div className={styles.text_container}>
              <p className={styles.title_text}>What's next?</p>
              <p className={styles.instruction}>1. Download the Myndlift app</p>
            </div>
            <div className={styles.actions_container}>
              <a
                href="https://apps.apple.com/il/app/myndlift/id1448710522"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  orderConfirmationPageEventLogger({
                    name: 'apple_store_click',
                  });
                }}
              >
                <img
                  src={downloadOnTheAppStoreImg}
                  className={styles.img_style}
                  // style={{ marginRight: 3.5 }}
                />
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.myndlift.production"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  orderConfirmationPageEventLogger({
                    name: 'google_play_click',
                  });
                }}
              >
                <img
                  src={getItOnGooglePlayImg}
                  className={styles.img_style}
                  // style={{ marginLeft: 3.5 }}
                />
              </a>
            </div>
            <div className={styles.text_container} style={{ marginBottom: 22 }}>
              <p className={styles.instruction2}>2. Insert activation code</p>
              <p className={styles.description}>
                When signing up on the Myndlift app, use the following code to
                activate your Myndlift account. This code will also be sent to
                your email.
              </p>
            </div>
            <div className={styles.activation_code_and_copy_btn_container}>
              <div className={styles.activation_code_container}>
                {[...activationCode].map((char, idx) => (
                  <div
                    className={styles.activation_code_char_box}
                    //eslint-disable-next-line react/no-array-index-key
                    key={`${char}_${idx}`}
                  >
                    <span
                      className={styles.activation_code_char}
                      style={{
                        fontSize: activationCode.length > 7 ? 30 : 'unset',
                      }}
                    >
                      {char}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  setIsCodeCopied(true);
                  copyToClipboard(activationCode);
                  orderConfirmationPageEventLogger({
                    name: 'copy_code_click',
                  });
                  setTimeout(() => {
                    setIsCodeCopied(false);
                  }, 3000);
                }}
                className={styles.copy_to_clipboard_btn}
                type="button"
              >
                <img src={featherCopyIcon} />
                <span>{isCodeCopied ? 'Code copied!' : 'Copy code'}</span>
              </button>
            </div>
            <div className={styles.separator_line_container}>
              <hr />
            </div>
            <div className={styles.family_members_discount_container}>
              <p className={styles.family_members_discount_title}>
                Hurray!{' '}
                <span role="img" aria-label="Partying Face">
                  🥳
                </span>
              </p>
              <p className={styles.family_members_discount_desc}>
                You’ve just unlocked 37% OFF on <i>family members.</i>
              </p>
              <p className={styles.family_members_discount_note}>
                P.S. You can share the same hardware but have different
                accounts.{' '}
              </p>
            </div>
            <div className={styles.btn_container}>
              <CustomBtn
                onClick={() => {
                  onAddMoreUser();
                  orderConfirmationPageEventLogger({
                    name: 'add_member_click',
                  });
                }}
              >
                <span style={{ paddingLeft: 32, paddingRight: 32 }}>
                  add a member
                </span>
              </CustomBtn>
            </div>
          </div>
          <div style={{ marginBottom: isMatchingXSExtraScreen ? 65 : 15 }}>
            <p style={{ margin: 0, fontSize: 12 }}>
              <span role="img" aria-label="Money Bag">
                💰
              </span>
              This order might be eligible for HSA/FSA reimbursement.{' '}
              <a
                href="https://app.truemed.com/qualify/tm_qual_5y4zi04w78?utm=post_purchase"
                style={{ color: '#087EF9' }}
                onClick={() => {
                  orderConfirmationPageEventLogger({
                    name: 'request_reimbursement_click',
                  });
                }}
              >
                Get Reimbursed
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

SuccessfulPaymentBase.propTypes = exact({
  // onVisitBlog: PropTypes.func.isRequired,
  // hasOnlySoftwarePackage: PropTypes.bool.isRequired,
  onAddMoreUser: PropTypes.func.isRequired,
  onComponentReadyToRender: PropTypes.func.isRequired,
  activationCode: PropTypes.string.isRequired,
});

export const SuccessfulPayment = React.memo(SuccessfulPaymentBase);
SuccessfulPayment.displayName = 'SuccessfulPayment';
